/**
 * Workshop inbound mailbox (PF-136).
 *
 * Shows the current address, copies it, and rotates it. The previous address
 * stays valid for 14 days. The mailbox copy address (#3735) follows when the
 * workshop has it switched on.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useInboundEmailAddress,
  useRotateInboundEmailAddress,
} from '@/hooks/useInboundDocuments';
import { useNotify } from '@/hooks/useNotify';
import { useAuthStore } from '@/stores/authStore';
import { InboundAddressDetails } from './InboundAddressDetails';
import { MailboxCopySection } from './MailboxCopySection';

const ADDRESS_GRACE_DAYS = 14;

export function InboundEmailSettingsTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canRotate = hasPermission('settings.edit_workshop');
  const addressQuery = useInboundEmailAddress();
  const rotateMutation = useRotateInboundEmailAddress();
  const [confirmRotate, setConfirmRotate] = useState(false);

  const handleRotate = async () => {
    try {
      await rotateMutation.mutateAsync();
      notify.success(t('inboundEmail.rotated'), t('inboundEmail.rotatedDesc', { days: ADDRESS_GRACE_DAYS }));
      setConfirmRotate(false);
    } catch {
      notify.error(tc('error'), t('inboundEmail.rotateFailed'));
    }
  };

  if (addressQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (addressQuery.isError) {
    return <Alert severity="error">{t('inboundEmail.loadFailed')}</Alert>;
  }

  const data = addressQuery.data;
  const configured = data?.configured === true && Boolean(data.address);

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" fontWeight={600}>
          {t('inboundEmail.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('inboundEmail.description')}
        </Typography>
      </Box>

      {!configured && (
        <Alert severity="info">{t('inboundEmail.notConfigured')}</Alert>
      )}

      {configured && data.address && (
        <InboundAddressDetails
          address={data.address}
          graceAddresses={data.graceAddresses ?? []}
          label={t('inboundEmail.addressLabel')}
          copyLabel={t('inboundEmail.copyAddress')}
          testId="inbound-email-address"
        />
      )}

      {canRotate && configured && (
        <Box>
          <Button
            variant="outlined"
            onClick={() => setConfirmRotate(true)}
            disabled={rotateMutation.isPending}
          >
            {t('inboundEmail.rotate')}
          </Button>
        </Box>
      )}

      <Alert severity="info">{t('inboundEmail.testerInstructions')}</Alert>

      <ConfirmDialog
        open={confirmRotate}
        title={t('inboundEmail.rotateTitle')}
        message={t('inboundEmail.rotateMessage', { days: ADDRESS_GRACE_DAYS })}
        confirmText={t('inboundEmail.rotate')}
        onConfirm={() => {
          void handleRotate();
        }}
        onCancel={() => setConfirmRotate(false)}
        loading={rotateMutation.isPending}
      />

      <MailboxCopySection canRotate={canRotate} />
    </Stack>
  );
}
